// Phase II · Wave D.6b · GeoRedirectHandler · first-visit locale banner.
//
// Mounts once at the root of <App />. On mount:
//   1. If the user already has a `glms_locale` cookie → no-op (silent respect).
//   2. Else fetch `/api/v9/i18n/geo-suggest` once.
//   3. If the suggested locale differs from the current URL locale AND
//      the user has NOT previously dismissed the banner → render a
//      dismissable top-strip offering to switch.
//
// UX contract:
//   · Non-blocking: banner never covers content, never redirects
//     automatically, never blocks navigation.
//   · One-time: dismissing (X) sets `glms_geo_dismissed=1` (30d TTL)
//     and the banner never shows again.
//   · V12 Achromatic Strict: zero saturated colour classes.
//
// Testid contract: `geo-redirect-banner`, `geo-redirect-accept`,
// `geo-redirect-decline`, `geo-redirect-dismiss`.
import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

import {
    DEFAULT_LOCALE, GLMS_LOCALE_CODES, LOCALE_COOKIE, LOCALE_COOKIE_TTL,
    isSkipPath, isValidLocale, normaliseLocale,
} from "@/i18n/wave_d1/glms_locales";
import { mapWaveDtoM14 } from "@/i18n/m14/legacyAdapter";
import { isM14LegacyEnabled } from "@/i18n/m14_feature_flag";

const DISMISS_COOKIE = "glms_geo_dismissed";
const DISMISS_TTL_DAYS = 30;
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Human labels (just the native name) used in banner copy. Falls back
// to the slug for exotic locales.
const LOCALE_LABEL = {
    "en-US": "English", "it": "Italiano", "de": "Deutsch", "fr": "Français",
    "es": "Español", "pt-BR": "Português", "zh-CN": "中文", "ja": "日本語",
    "ko": "한국어", "hi": "हिन्दी", "ar": "العربية", "ru": "Русский",
    "nl": "Nederlands", "pl": "Polski", "tr": "Türkçe", "el": "Ελληνικά",
    "he": "עברית", "th": "ไทย", "vi": "Tiếng Việt", "id": "Bahasa Indonesia",
};

function currentLocaleFromUrl() {
    if (typeof window === "undefined" || !window.location) return DEFAULT_LOCALE;
    const path = window.location.pathname || "/";
    if (isSkipPath(path)) return DEFAULT_LOCALE;
    const m = path.match(/^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?)(\/|$)/);
    if (m && GLMS_LOCALE_CODES.includes(m[1])) return m[1];
    if (m && isValidLocale(m[1])) return normaliseLocale(m[1]);
    return DEFAULT_LOCALE;
}

function labelFor(code) {
    return LOCALE_LABEL[code] || code;
}

async function fetchGeoSuggestion() {
    try {
        const url = `${BACKEND_URL}/api/v9/i18n/geo-suggest`;
        const res = await fetch(url, {
            credentials: "include",
            headers: { "Accept": "application/json" },
        });
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

/**
 * Phase II · Wave E.0 · Fire-and-forget banner analytics emit.
 * Never throws · never blocks the UI · errors swallowed.
 */
function emitBannerEvent(payload) {
    try {
        const url = `${BACKEND_URL}/api/v9/i18n/geo-banner/event`;
        fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => { /* fire-and-forget */ });
    } catch { /* noop */ }
}

export default function GeoRedirectHandler() {
    const [banner, setBanner] = useState(null); // {suggested, current}
    const didRunRef = useRef(false);

    useEffect(() => {
        if (didRunRef.current) return;
        didRunRef.current = true;
        if (typeof window === "undefined" || !window.location) return;

        // 1 · User already has a locale cookie → respect choice.
        try {
            const existing = Cookies.get(LOCALE_COOKIE);
            if (existing && isValidLocale(existing)) {
                return;
            }
        } catch { /* storage disabled */ }

        // 2 · Dismissed previously → respect choice.
        try {
            if (Cookies.get(DISMISS_COOKIE) === "1") return;
        } catch { /* noop */ }

        // 3 · Skip path (/api, static) → do nothing.
        if (isSkipPath(window.location.pathname)) return;

        // 4 · Fetch suggestion.
        (async () => {
            const data = await fetchGeoSuggestion();
            if (!data || !data.suggested_locale) return;
            const suggested = normaliseLocale(data.suggested_locale);
            const current = currentLocaleFromUrl();
            // Only prompt when:
            //   · suggestion differs from current locale
            //   · source is NOT "default" (no suggestion worth asking about)
            //   · suggestion is a known Wave-D locale
            if (
                suggested !== current
                && data.source !== "default"
                && GLMS_LOCALE_CODES.includes(suggested)
            ) {
                setBanner({
                    suggested, current,
                    country: data.country || null,
                });
            }
        })();
    }, []);

    const accept = useCallback(() => {
        if (!banner) return;
        const { suggested, current, country } = banner;
        emitBannerEvent({
            suggested_locale: suggested, current_locale: current,
            event_type: "accept", country_code: country,
        });
        // Phase II · Wave E.1b · Smart A/B hook: suggest the matching
        // currency on landing via `?openCurrency=1` URL param that the
        // `CurrencySwitcher` reads on mount to auto-open its dropdown.
        // Pure best-effort · guarded by env `REACT_APP_E1B_AUTO_OPEN=1`
        // so we can disable the experiment without a redeploy.
        emitBannerEvent({
            suggested_locale: suggested, current_locale: current,
            event_type: "currency_switcher_auto_opened", country_code: country,
        });
        try {
            Cookies.set(LOCALE_COOKIE, suggested, {
                expires: LOCALE_COOKIE_TTL, path: "/",
                sameSite: "lax", secure: true,
            });
            // Phase II · Wave E.1c.4 · TD6 · M14 dual-write gated.
            if (isM14LegacyEnabled()) {
                const m14 = mapWaveDtoM14(suggested);
                Cookies.set("glms_lang", m14, {
                    expires: LOCALE_COOKIE_TTL, path: "/",
                    sameSite: "lax", secure: true,
                });
                try { window.localStorage.setItem("glms_lang", m14); }
                catch { /* storage disabled */ }
            }
        } catch { /* storage disabled */ }
        // Navigate to the suggested locale prefix (reload once so SSR +
        // basename resolver boot into the right state).
        try {
            const path = window.location.pathname || "/";
            const stripped = path.replace(
                /^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?)(?=\/|$)/,
                "",
            ) || "/";
            const sep = (window.location.search || "").includes("?")
                ? "&" : "?";
            const extra = `${sep}openCurrency=1`;
            window.location.href = `/${suggested}${stripped}${window.location.search || ""}${extra}${window.location.hash || ""}`;
        } catch { /* noop */ }
    }, [banner]);

    const decline = useCallback(() => {
        if (!banner) return;
        emitBannerEvent({
            suggested_locale: banner.suggested,
            current_locale: banner.current,
            event_type: "keep",
            country_code: banner.country,
        });
        try {
            Cookies.set(LOCALE_COOKIE, banner.current, {
                expires: LOCALE_COOKIE_TTL, path: "/",
                sameSite: "lax", secure: true,
            });
            Cookies.set(DISMISS_COOKIE, "1", {
                expires: DISMISS_TTL_DAYS, path: "/",
                sameSite: "lax", secure: true,
            });
        } catch { /* storage disabled */ }
        setBanner(null);
    }, [banner]);

    const dismiss = useCallback(() => {
        if (banner) {
            emitBannerEvent({
                suggested_locale: banner.suggested,
                current_locale: banner.current,
                event_type: "dismiss",
                country_code: banner.country,
            });
        }
        try {
            Cookies.set(DISMISS_COOKIE, "1", {
                expires: DISMISS_TTL_DAYS, path: "/",
                sameSite: "lax", secure: true,
            });
        } catch { /* storage disabled */ }
        setBanner(null);
    }, [banner]);

    if (!banner) return null;
    const suggestedLabel = labelFor(banner.suggested);
    const currentLabel = labelFor(banner.current);

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Language suggestion"
            data-testid="geo-redirect-banner"
            className={
                "fixed top-0 inset-x-0 z-[70] flex items-center justify-between "
                + "gap-3 border-b border-neutral-800 bg-neutral-950/95 "
                + "backdrop-blur px-4 py-2 text-sm text-neutral-200 "
                + "shadow-sm shadow-black/30"
            }
        >
            <div className="flex-1 flex items-center gap-2 min-w-0">
                <svg
                    width="14" height="14" viewBox="0 0 20 20"
                    aria-hidden="true" className="shrink-0 text-neutral-500"
                >
                    <path
                        d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 2a6 6 0 110 12 6 6 0 010-12zm-1 2v4l3 2 .7-1.2L10.5 9.5V6H9z"
                        fill="currentColor"
                    />
                </svg>
                <span className="truncate">
                    <span className="text-neutral-400">Looks like you&apos;re viewing in </span>
                    <span className="font-medium">{currentLabel}</span>
                    <span className="text-neutral-400">. Switch to </span>
                    <span className="font-medium">{suggestedLabel}</span>
                    <span className="text-neutral-400">?</span>
                </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    onClick={accept}
                    data-testid="geo-redirect-accept"
                    className={
                        "rounded-md border-2 border-neutral-300 bg-transparent "
                        + "px-3 py-1 text-xs font-medium text-neutral-100 "
                        + "hover:bg-neutral-800 transition"
                    }
                >
                    Switch to {suggestedLabel}
                </button>
                <button
                    type="button"
                    onClick={decline}
                    data-testid="geo-redirect-decline"
                    className={
                        "rounded-md border border-neutral-700 bg-transparent "
                        + "px-3 py-1 text-xs font-medium text-neutral-300 "
                        + "hover:bg-neutral-900 transition"
                    }
                >
                    Keep {currentLabel}
                </button>
                <button
                    type="button"
                    onClick={dismiss}
                    data-testid="geo-redirect-dismiss"
                    aria-label="Close"
                    className={
                        "rounded border border-neutral-800 bg-transparent "
                        + "p-1 text-neutral-500 hover:text-neutral-200 "
                        + "transition"
                    }
                >
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                        <path
                            d="M2 2 L10 10 M10 2 L2 10"
                            stroke="currentColor" strokeWidth="1.6" fill="none"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
}
